/**
 * Project Selector Component
 *
 * Dropdown for selecting a project to filter data across all tabs
 */
import React, { useEffect, useState, useRef } from 'react'
import { observer } from 'mobx-react-lite'
import { ChevronDownIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { dataStore } from '@/stores/DataStore'
import clsx from 'clsx'

interface ProjectSelectorProps {
  className?: string
}

const ProjectSelector: React.FC<ProjectSelectorProps> = observer(({ className }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    // Fetch projects on mount
    if (dataStore.projects.length === 0) {
      dataStore.fetchProjects()
    }
  }, [])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const filteredProjects = dataStore.projects.filter(
    (project) =>
      project.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      project.description?.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const handleSelectProject = (projectId: string | null) => {
    dataStore.setSelectedProjectId(projectId)
    setIsOpen(false)
    setSearchQuery('')
  }

  const selectedProject = dataStore.selectedProject

  return (
    <div ref={dropdownRef} className={clsx('relative', className)}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={clsx(
          'flex items-center justify-between gap-2 px-4 py-2 min-w-[200px]',
          'border border-gray-300 rounded-lg bg-white',
          'hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500',
          'text-sm font-medium text-gray-700'
        )}
      >
        <span className="truncate">
          {selectedProject ? selectedProject.name : 'All Projects'}
        </span>
        <ChevronDownIcon
          className={clsx('w-4 h-4 text-gray-400 transition-transform', isOpen && 'rotate-180')}
        />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-72 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
          {/* Search input */}
          <div className="p-2 border-b border-gray-100">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search projects..."
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                autoFocus
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2"
                >
                  <XMarkIcon className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                </button>
              )}
            </div>
          </div>

          {/* Options */}
          <div className="max-h-60 overflow-y-auto py-1">
            {/* All Projects option */}
            <button
              onClick={() => handleSelectProject(null)}
              className={clsx(
                'w-full px-4 py-2 text-left text-sm',
                'hover:bg-gray-100 transition-colors',
                !dataStore.selectedProjectId && 'bg-primary-50 text-primary-700 font-medium'
              )}
            >
              All Projects
            </button>

            {/* Divider */}
            <div className="border-t border-gray-100 my-1" />

            {/* Projects list */}
            {dataStore.isLoadingProjects ? (
              <div className="px-4 py-3 text-sm text-gray-500 text-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600 mx-auto mb-2" />
                Loading projects...
              </div>
            ) : filteredProjects.length === 0 ? (
              <div className="px-4 py-3 text-sm text-gray-500 text-center">
                {searchQuery ? 'No projects found' : 'No projects available'}
              </div>
            ) : (
              filteredProjects.map((project) => (
                <button
                  key={project.id}
                  onClick={() => handleSelectProject(project.id)}
                  className={clsx(
                    'w-full px-4 py-2 text-left',
                    'hover:bg-gray-100 transition-colors',
                    dataStore.selectedProjectId === project.id &&
                      'bg-primary-50 text-primary-700'
                  )}
                >
                  <div className="text-sm font-medium truncate">{project.name}</div>
                  {project.description && (
                    <div className="text-xs text-gray-500 truncate">{project.description}</div>
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  )
})

ProjectSelector.displayName = 'ProjectSelector'

export default ProjectSelector
